/**
 * OrdenFormDialog - Formulario de creación de órdenes
 * Selección de paciente, médico, exámenes/perfiles, prioridad y tipo de entrega
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Search, X, TestTubes, Package, Calculator, Percent, Gift } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useApiError } from "@/hooks/use-api-error";
import { useBuscarPacientes } from "@/hooks/use-pacientes";
import {
  useCreateOrden,
  useBuscarExamenesTipo,
  useBuscarPerfiles,
  useMarcarCortesia,
  type ExamenTipoListItem,
  type PerfilExamenListItem,
  type Orden,
} from "@/hooks/use-ordenes";
import { useBuscarMedicosRemitentes } from "@/hooks/use-medicos-remitentes";
import type { MedicoRemitenteListItem } from "@/hooks/use-medicos-remitentes";
import { useBuscarClientes } from "@/hooks/use-clientes";
import type { ClienteListItem } from "@/hooks/use-clientes";
import { useListasPrecios } from "@/hooks/use-facturacion";
import { useSucursal } from "@/contexts/sucursal-context";
import { useAuth } from "@/contexts/auth-context";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import {
  shouldShowCreateOption,
  getAutoListaPrecioFromCliente,
} from "../utils/inline-creation-utils";
import { InlineMedicoForm } from "./inline-medico-form";
import { InlineClienteForm } from "./inline-cliente-form";
import { useDebounce } from "@/hooks/use-debounce";
import { calcularPrecioAjustado, formatListaPrecioLabel } from "../utils/precio-utils";
import { ReciboPostCreacionDialog } from "./recibo-post-creacion-dialog";
import { PagoOrdenFormDialog } from "./pago-orden-form-dialog";
import type { PrioridadOrden, TipoEntrega, TipoServicio, OrdenListItem } from "@/hooks/use-ordenes";

interface OrdenFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  initialPacienteId?: number | null;
  initialPacienteNombre?: string;
  /** Descripción pre-formateada: identificacion · edad_texto · Tel: celular */
  initialPacienteDesc?: string;
  /** Edad en años para aplicar descuento automático de adulto mayor */
  initialPacienteEdadYears?: number;
}

export function OrdenFormDialog({
  open,
  onOpenChange,
  onSuccess,
  initialPacienteId,
  initialPacienteNombre,
  initialPacienteDesc,
  initialPacienteEdadYears,
}: OrdenFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const { sucursalActiva, isAllSucursalesSelected } = useSucursal();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();

  // Form state
  const [pacienteId, setPacienteId] = useState<number | null>(null);
  const [pacienteNombre, setPacienteNombre] = useState("");
  const [pacienteDesc, setPacienteDesc] = useState("");
  const [medicoId, setMedicoId] = useState<number | null>(null);
  const [medicoNombre, setMedicoNombre] = useState("");
  const [clienteId, setClienteId] = useState<number | null>(null);
  const [clienteNombre, setClienteNombre] = useState("");
  const [listaPrecioId, setListaPrecioId] = useState<number | null>(null);
  const [listaPrecioAutoSelected, setListaPrecioAutoSelected] = useState(false);
  const [selectedExamenes, setSelectedExamenes] = useState<ExamenTipoListItem[]>([]);
  const [selectedPerfiles, setSelectedPerfiles] = useState<PerfilExamenListItem[]>([]);
  const [prioridad, setPrioridad] = useState<PrioridadOrden>("NORMAL");
  const [tipoEntrega, setTipoEntrega] = useState<TipoEntrega>("PRESENCIAL");
  const [tipoServicio, setTipoServicio] = useState<TipoServicio>("AMBULATORIO");
  const [observaciones, setObservaciones] = useState("");
  const [descuentoPorcentaje, setDescuentoPorcentaje] = useState("");
  const [descuentoLempiras, setDescuentoLempiras] = useState("");

  // Search state
  const [pacienteSearch, setPacienteSearch] = useState("");
  const [medicoSearch, setMedicoSearch] = useState("");
  const [clienteSearch, setClienteSearch] = useState("");
  const [examenSearch, setExamenSearch] = useState("");
  const [perfilSearch, setPerfilSearch] = useState("");
  const debouncedPacienteSearch = useDebounce(pacienteSearch, 300);
  const debouncedMedicoSearch = useDebounce(medicoSearch, 300);
  const debouncedClienteSearch = useDebounce(clienteSearch, 300);
  const debouncedExamenSearch = useDebounce(examenSearch, 300);
  const debouncedPerfilSearch = useDebounce(perfilSearch, 300);

  // Exit confirmation state
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Inline creation state
  const [showInlineMedico, setShowInlineMedico] = useState(false);
  const [showInlineCliente, setShowInlineCliente] = useState(false);

  // Permisos para creación inline
  const canCreateMedico = hasPermission("medicos_remitentes", "create");
  const canCreateCliente = hasPermission("clientes", "create");

  // Flujo post-creación: confirm-pago → pago → recibo
  const [postCreacion, setPostCreacion] = useState<{
    step: "idle" | "confirm-pago" | "pago" | "recibo";
    orden: Orden | null;
  }>({ step: "idle", orden: null });
  // Ref para evitar que onOpenChange del AlertDialog sobreescriba el step al confirmar
  const confirmPagoConfirmedRef = useRef(false);
  // Ref para indicar que el cierre del dialog es programático (guardado exitoso)
  // y no debe disparar el confirm de "¿Deseas cancelar?"
  const isSavingRef = useRef(false);

  // Cortesía: campo visible cuando total=0
  const [autorizadoPor, setAutorizadoPor] = useState(""); // Queries
  const { data: listasPrecios } = useListasPrecios();
  const { data: pacientesResults } = useBuscarPacientes(debouncedPacienteSearch, {
    enabled: debouncedPacienteSearch.length >= 2 && !pacienteId,
  });
  const { data: medicosResults } = useBuscarMedicosRemitentes(debouncedMedicoSearch, {
    enabled: debouncedMedicoSearch.length >= 2 && !medicoId,
  });
  const { data: clientesResults } = useBuscarClientes(debouncedClienteSearch, {
    enabled: debouncedClienteSearch.length >= 2 && !clienteId,
  });
  const { data: examenesResults } = useBuscarExamenesTipo(debouncedExamenSearch, {
    enabled: debouncedExamenSearch.length >= 2,
  });
  const { data: perfilesResults } = useBuscarPerfiles(debouncedPerfilSearch, {
    enabled: debouncedPerfilSearch.length >= 2,
  });

  // Mutation
  const marcarCortesiaMutation = useMarcarCortesia();

  const createMutation = useCreateOrden({
    onSuccess: (orden) => {
      isSavingRef.current = true;
      onOpenChange(false);
      isSavingRef.current = false;
      const totalOrden = orden.examenes.reduce((s, e) => s + Number(e.precio), 0);
      const descPct = totalOrden * (Number(orden.descuento_porcentaje) / 100);
      const totalNeto = Math.max(totalOrden - descPct - Number(orden.descuento_lempiras), 0);

      if (totalNeto === 0 && autorizadoPor.trim()) {
        // Marcar como cortesía automáticamente y luego ir al recibo
        marcarCortesiaMutation.mutate(
          { id: orden.id, autorizado_por: autorizadoPor.trim() },
          {
            onSuccess: () => setPostCreacion({ step: "recibo", orden }),
            onError: () => setPostCreacion({ step: "recibo", orden }),
          }
        );
      } else {
        const nextStep = totalNeto === 0 ? "recibo" : "confirm-pago";
        setPostCreacion({ step: nextStep, orden });
      }
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setPacienteId(initialPacienteId ?? null);
      setPacienteNombre(initialPacienteNombre ?? "");
      setPacienteDesc(initialPacienteDesc ?? "");
      setMedicoId(null);
      setMedicoNombre("");
      setClienteId(null);
      setClienteNombre("");
      setListaPrecioId(null);
      setListaPrecioAutoSelected(false);
      setSelectedExamenes([]);
      setSelectedPerfiles([]);
      setPrioridad("NORMAL");
      setTipoEntrega("PRESENCIAL");
      setTipoServicio("AMBULATORIO");
      setObservaciones("");
      // Auto-aplicar descuento del 30% para pacientes de 60 años o más
      if (initialPacienteEdadYears !== undefined && initialPacienteEdadYears >= 60) {
        setDescuentoPorcentaje("30");
      } else {
        setDescuentoPorcentaje("");
      }
      setDescuentoLempiras("");
      setAutorizadoPor("");
      setPacienteSearch("");
      setMedicoSearch("");
      setClienteSearch("");
      setExamenSearch("");
      setPerfilSearch("");
    }
  }, [
    open,
    initialPacienteId,
    initialPacienteNombre,
    initialPacienteDesc,
    initialPacienteEdadYears,
  ]);

  const handleSelectPaciente = useCallback(
    (id: number, nombre: string, desc: string, edadYears?: number) => {
      setPacienteId(id);
      setPacienteNombre(nombre);
      setPacienteDesc(desc);
      setPacienteSearch("");

      // Auto-aplicar descuento del 30% para pacientes de 60 años o más
      if (edadYears !== undefined && edadYears >= 60) {
        setDescuentoPorcentaje("30");
        toast.info(t("ordenes.discount.autoSenior", { edad: edadYears }), { duration: 5000 });
      }
    },
    [t]
  );
  const listaPrecioActiva = useMemo(
    () => listasPrecios?.find((lp) => lp.id === listaPrecioId) ?? null,
    [listasPrecios, listaPrecioId]
  );
  const porcentajeAjuste = listaPrecioActiva?.porcentaje_ajuste ?? 0;

  // Collect all exam IDs already covered by selected profiles
  const perfilExamenIds = useMemo(
    () => new Set(selectedPerfiles.flatMap((p) => p.examenes_ids ?? [])),
    [selectedPerfiles]
  );

  const handleAddExamen = useCallback(
    (examen: ExamenTipoListItem) => {
      // Block if this exam is already included in a selected profile
      if (perfilExamenIds.has(examen.id)) {
        toast.warning(t("ordenes.duplicateExam.alreadyInProfile"));
        return;
      }
      setSelectedExamenes((prev) => {
        if (prev.some((e) => e.id === examen.id)) return prev;
        return [...prev, examen];
      });
      setExamenSearch("");
    },
    [perfilExamenIds, t]
  );

  const handleRemoveExamen = useCallback((id: number) => {
    setSelectedExamenes((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const handleAddPerfil = useCallback(
    (perfil: PerfilExamenListItem) => {
      setSelectedPerfiles((prev) => {
        if (prev.some((p) => p.id === perfil.id)) return prev;
        return [...prev, perfil];
      });
      // Auto-remove individual exams that are included in this profile
      const perfilIds = new Set(perfil.examenes_ids ?? []);
      setSelectedExamenes((prev) => {
        const duplicados = prev.filter((e) => perfilIds.has(e.id));
        if (duplicados.length > 0) {
          toast.info(
            t("ordenes.duplicateExam.removedFromList", {
              count: duplicados.length,
              perfil: perfil.nombre,
            })
          );
          return prev.filter((e) => !perfilIds.has(e.id));
        }
        return prev;
      });
      setPerfilSearch("");
    },
    [t]
  );

  const handleRemovePerfil = useCallback((id: number) => {
    setSelectedPerfiles((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const hasUnsavedData =
    !!pacienteId || !!medicoId || selectedExamenes.length > 0 || selectedPerfiles.length > 0;

  const handleOpenChange = useCallback(
    (value: boolean) => {
      if (!value && hasUnsavedData && !isSavingRef.current) {
        setShowExitConfirm(true);
        return;
      }
      onOpenChange(value);
    },
    [hasUnsavedData, onOpenChange]
  );

  const handleConfirmExit = useCallback(() => {
    setShowExitConfirm(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleMedicoCreado = (medico: MedicoRemitenteListItem) => {
    setMedicoId(medico.id);
    setMedicoNombre(medico.nombre_completo);
    setMedicoSearch("");
    setShowInlineMedico(false);
    queryClient.invalidateQueries({ queryKey: queryKeys.medicosRemitentes.all });
    toast.success(t("ordenes.inline.doctorCreated", { nombre: medico.nombre_completo }));
  };

  const handleClienteCreado = (cliente: ClienteListItem) => {
    setClienteId(cliente.id);
    setClienteNombre(cliente.nombre);
    setClienteSearch("");
    setShowInlineCliente(false);
    // Auto-seleccionar lista de precios del cliente si tiene una asignada
    const listaPrecioAuto = getAutoListaPrecioFromCliente(cliente);
    if (listaPrecioAuto !== null) {
      setListaPrecioId(listaPrecioAuto);
      setListaPrecioAutoSelected(true);
    }
    queryClient.invalidateQueries({ queryKey: queryKeys.clientes.all });
    toast.success(t("ordenes.inline.clientCreated", { nombre: cliente.nombre }));
  };

  const handleSubmit = () => {
    if (!pacienteId) return;
    if (!sucursalActiva?.id) {
      toast.error(t("ordenes.mustSelectSpecificBranch"));
      return;
    }
    if (selectedExamenes.length === 0 && selectedPerfiles.length === 0) {
      toast.error(t("ordenes.mustSelectExam"));
      return;
    }

    createMutation.mutate({
      paciente: pacienteId,
      sucursal: sucursalActiva.id,
      medico_remitente: medicoId,
      cliente: clienteId,
      lista_precio: listaPrecioId,
      examenes_ids: selectedExamenes.map((e) => e.id),
      perfiles_ids: selectedPerfiles.map((p) => p.id),
      prioridad,
      tipo_entrega: tipoEntrega,
      tipo_servicio: tipoServicio,
      observaciones,
      descuento_porcentaje: parseFloat(descuentoPorcentaje) || 0,
      descuento_lempiras: parseFloat(descuentoLempiras) || 0,
    });
  };

  // Filter out already selected items and exams covered by profiles
  const filteredExamResults = examenesResults?.filter(
    (e) => !selectedExamenes.some((s) => s.id === e.id) && !perfilExamenIds.has(e.id)
  );
  const filteredPerfilResults = perfilesResults?.filter(
    (p) => !selectedPerfiles.some((s) => s.id === p.id)
  );

  // Calculate totals with lista de precios adjustment
  const totalExamenesBase = selectedExamenes.reduce((sum, e) => sum + Number(e.precio), 0);
  const totalExamenesAjustado = selectedExamenes.reduce(
    (sum, e) => sum + calcularPrecioAjustado(Number(e.precio), porcentajeAjuste),
    0
  );
  const totalPerfilesBase = selectedPerfiles.reduce((sum, p) => sum + Number(p.precio), 0);
  const totalPerfilesAjustado = selectedPerfiles.reduce(
    (sum, p) => sum + calcularPrecioAjustado(Number(p.precio), porcentajeAjuste),
    0
  );
  const totalExamenes = totalExamenesAjustado;
  const totalPerfiles = totalPerfilesAjustado;
  const subtotalBase = totalExamenesBase + totalPerfilesBase;
  const subtotal = totalExamenes + totalPerfiles;
  const ajusteListaPrecio = subtotal - subtotalBase; // positivo = aumento, negativo = descuento

  // Apply discounts: percentage first, then fixed Lempiras
  const pctVal = parseFloat(descuentoPorcentaje) || 0;
  const lpsVal = parseFloat(descuentoLempiras) || 0;
  const afterPct = subtotal * (1 - Math.min(Math.max(pctVal, 0), 100) / 100);
  const totalGeneral = Math.max(afterPct - Math.max(lpsVal, 0), 0);

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            if (hasUnsavedData) {
              e.preventDefault();
              setShowExitConfirm(true);
            }
          }}
          onEscapeKeyDown={(e) => {
            if (hasUnsavedData) {
              e.preventDefault();
              setShowExitConfirm(true);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{t("ordenes.newOrder")}</DialogTitle>
            <DialogDescription>{t("ordenes.description")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Paciente selector */}
            <div className="space-y-2">
              <Label>{t("ordenes.fields.patient")} *</Label>
              {pacienteId ? (
                <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{pacienteNombre}</p>
                    {pacienteDesc && (
                      <p className="text-xs text-muted-foreground font-mono">{pacienteDesc}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={() => {
                      setPacienteId(null);
                      setPacienteNombre("");
                      setPacienteDesc("");
                      setDescuentoPorcentaje("");
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t("ordenes.searchPatient")}
                      value={pacienteSearch}
                      onChange={(e) => setPacienteSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {pacientesResults && pacientesResults.length > 0 && (
                    <div className="border rounded-md max-h-40 overflow-y-auto">
                      {pacientesResults.map((p) => (
                        <button
                          key={p.id}
                          className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex flex-col gap-0.5"
                          onClick={() =>
                            handleSelectPaciente(
                              p.id,
                              p.nombre_completo,
                              [
                                p.identificacion,
                                p.edad_texto,
                                p.celular ? `Tel: ${p.celular}` : null,
                              ]
                                .filter(Boolean)
                                .join(" · "),
                              p.edad_years
                            )
                          }
                        >
                          <span className="font-medium">{p.nombre_completo}</span>
                          <span className="text-muted-foreground text-xs font-mono">
                            {p.identificacion}
                            {p.edad_texto ? ` · ${p.edad_texto}` : ""}
                            {p.celular ? ` · Tel: ${p.celular}` : ""}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Cliente selector */}
            <div className="space-y-2">
              <Label>{t("ordenes.fields.client")}</Label>
              {clienteId ? (
                <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
                  <span className="flex-1 font-medium">{clienteNombre}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      setClienteId(null);
                      setClienteNombre("");
                      // Limpiar lista de precios si fue auto-seleccionada
                      if (listaPrecioAutoSelected) {
                        setListaPrecioId(null);
                        setListaPrecioAutoSelected(false);
                      }
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t("ordenes.searchClient")}
                      value={clienteSearch}
                      onChange={(e) => {
                        setClienteSearch(e.target.value);
                        setShowInlineCliente(false);
                      }}
                      className="pl-9"
                    />
                  </div>
                  {showInlineCliente ? (
                    <InlineClienteForm
                      initialName={clienteSearch}
                      sucursalId={
                        !isAllSucursalesSelected && sucursalActiva ? sucursalActiva.id : 0
                      }
                      onSuccess={handleClienteCreado}
                      onCancel={() => setShowInlineCliente(false)}
                    />
                  ) : (
                    <>
                      {clientesResults && clientesResults.length > 0 && (
                        <div className="border rounded-md max-h-40 overflow-y-auto">
                          {clientesResults.map((c) => (
                            <button
                              key={c.id}
                              className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between"
                              onClick={() => {
                                setClienteId(c.id);
                                setClienteNombre(c.nombre);
                                setClienteSearch("");
                                if (c.lista_precio) {
                                  setListaPrecioId(c.lista_precio);
                                  setListaPrecioAutoSelected(true);
                                }
                              }}
                            >
                              <span className="font-medium">{c.nombre}</span>
                              <span className="text-muted-foreground font-mono text-xs">
                                {c.codigo}
                              </span>
                            </button>
                          ))}
                          {shouldShowCreateOption(canCreateCliente, clienteSearch) && (
                            <button
                              className="w-full text-left px-3 py-2 hover:bg-muted text-sm text-primary font-medium border-t"
                              onClick={() => setShowInlineCliente(true)}
                              data-testid="inline-cliente-create-option"
                            >
                              + {t("ordenes.inline.createClient")}
                            </button>
                          )}
                        </div>
                      )}
                      {shouldShowCreateOption(canCreateCliente, clienteSearch) &&
                        (!clientesResults || clientesResults.length === 0) && (
                          <div className="border rounded-md">
                            <button
                              className="w-full text-left px-3 py-2 hover:bg-muted text-sm text-primary font-medium"
                              onClick={() => setShowInlineCliente(true)}
                              data-testid="inline-cliente-create-option"
                            >
                              + {t("ordenes.inline.createClient")}
                            </button>
                          </div>
                        )}
                    </>
                  )}
                  <p className="text-xs text-muted-foreground">{t("ordenes.clientHint")}</p>
                </div>
              )}
            </div>

            {/* Lista de Precios selector */}
            <div className="space-y-2">
              <Label>{t("ordenes.listaPrecio.label")}</Label>
              <Select
                value={listaPrecioId?.toString() ?? "none"}
                onValueChange={(v) => {
                  if (v === "none") {
                    setListaPrecioId(null);
                    setListaPrecioAutoSelected(false);
                  } else {
                    setListaPrecioId(Number(v));
                    setListaPrecioAutoSelected(false);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("ordenes.listaPrecio.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("ordenes.listaPrecio.standard")}</SelectItem>
                  {listasPrecios?.map((lp) => (
                    <SelectItem key={lp.id} value={lp.id.toString()}>
                      {formatListaPrecioLabel(lp.nombre, lp.porcentaje_ajuste)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {listaPrecioAutoSelected && listaPrecioActiva && (
                <p className="text-xs text-muted-foreground">
                  {t("ordenes.listaPrecio.autoSelected", { nombre: listaPrecioActiva.nombre })}
                </p>
              )}
            </div>

            {/* Médico remitente selector */}
            <div className="space-y-2">
              <Label>{t("ordenes.fields.doctor")}</Label>
              {medicoId ? (
                <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
                  <span className="flex-1 font-medium">{medicoNombre}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      setMedicoId(null);
                      setMedicoNombre("");
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t("ordenes.searchDoctor")}
                      value={medicoSearch}
                      onChange={(e) => {
                        setMedicoSearch(e.target.value);
                        setShowInlineMedico(false);
                      }}
                      className="pl-9"
                    />
                  </div>
                  {showInlineMedico ? (
                    <InlineMedicoForm
                      initialName={medicoSearch}
                      onSuccess={handleMedicoCreado}
                      onCancel={() => setShowInlineMedico(false)}
                    />
                  ) : (
                    <>
                      {medicosResults && medicosResults.length > 0 && (
                        <div className="border rounded-md max-h-40 overflow-y-auto">
                          {medicosResults.map((m) => (
                            <button
                              key={m.id}
                              className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between"
                              onClick={() => {
                                setMedicoId(m.id);
                                setMedicoNombre(m.nombre_completo);
                                setMedicoSearch("");
                              }}
                            >
                              <span className="font-medium">{m.nombre_completo}</span>
                              <span className="text-muted-foreground text-xs">
                                {m.especialidad}
                              </span>
                            </button>
                          ))}
                          {shouldShowCreateOption(canCreateMedico, medicoSearch) && (
                            <button
                              className="w-full text-left px-3 py-2 hover:bg-muted text-sm text-primary font-medium border-t"
                              onClick={() => setShowInlineMedico(true)}
                              data-testid="inline-medico-create-option"
                            >
                              + {t("ordenes.inline.createDoctor")}
                            </button>
                          )}
                        </div>
                      )}
                      {shouldShowCreateOption(canCreateMedico, medicoSearch) &&
                        (!medicosResults || medicosResults.length === 0) && (
                          <div className="border rounded-md">
                            <button
                              className="w-full text-left px-3 py-2 hover:bg-muted text-sm text-primary font-medium"
                              onClick={() => setShowInlineMedico(true)}
                              data-testid="inline-medico-create-option"
                            >
                              + {t("ordenes.inline.createDoctor")}
                            </button>
                          </div>
                        )}
                    </>
                  )}
                  <p className="text-xs text-muted-foreground">{t("ordenes.doctorHint")}</p>
                </div>
              )}
            </div>

            {/* Prioridad, Tipo de Entrega y Tipo de Servicio */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t("ordenes.fields.priority")}</Label>
                <Select value={prioridad} onValueChange={(v) => setPrioridad(v as PrioridadOrden)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NORMAL">{t("ordenes.priority.normal")}</SelectItem>
                    <SelectItem value="URGENTE">{t("ordenes.priority.urgent")}</SelectItem>
                    <SelectItem value="STAT">{t("ordenes.priority.stat")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("ordenes.fields.deliveryType")}</Label>
                <Select value={tipoEntrega} onValueChange={(v) => setTipoEntrega(v as TipoEntrega)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRESENCIAL">{t("ordenes.delivery.presencial")}</SelectItem>
                    <SelectItem value="EMAIL">{t("ordenes.delivery.email")}</SelectItem>
                    <SelectItem value="WHATSAPP">{t("ordenes.delivery.whatsapp")}</SelectItem>
                    <SelectItem value="PORTAL">{t("ordenes.delivery.portal")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("ordenes.fields.serviceType")}</Label>
                <Select
                  value={tipoServicio}
                  onValueChange={(v) => setTipoServicio(v as TipoServicio)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AMBULATORIO">
                      {t("ordenes.serviceType.ambulatorio")}
                    </SelectItem>
                    <SelectItem value="EMERGENCIA">
                      {t("ordenes.serviceType.emergencia")}
                    </SelectItem>
                    <SelectItem value="HOSPITALIZACION">
                      {t("ordenes.serviceType.hospitalizacion")}
                    </SelectItem>
                    <SelectItem value="DOMICILIO">{t("ordenes.serviceType.domicilio")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Tabs para Exámenes y Perfiles */}
            <Tabs defaultValue="examenes" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="examenes" className="flex items-center gap-2">
                  <TestTubes className="h-4 w-4" />
                  {t("ordenes.fields.exams")}
                </TabsTrigger>
                <TabsTrigger value="perfiles" className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  {t("ordenes.fields.profiles")}
                </TabsTrigger>
              </TabsList>

              {/* Tab de Exámenes */}
              <TabsContent value="examenes" className="space-y-3">
                <div className="space-y-2">
                  <Label>{t("ordenes.fields.exams")}</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t("ordenes.searchExams")}
                      value={examenSearch}
                      onChange={(e) => setExamenSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {filteredExamResults && filteredExamResults.length > 0 && (
                    <div className="border rounded-md max-h-40 overflow-y-auto">
                      {filteredExamResults.map((e) => (
                        <button
                          key={e.id}
                          className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between items-center"
                          onClick={() => handleAddExamen(e)}
                        >
                          <div>
                            <span className="font-mono text-xs text-muted-foreground mr-2">
                              {e.codigo}
                            </span>
                            <span>{e.nombre}</span>
                          </div>
                          <span className="text-muted-foreground">
                            L {Number(e.precio).toFixed(2)}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected exams */}
                {selectedExamenes.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>
                        {t("ordenes.selectedExams")} ({selectedExamenes.length})
                      </Label>
                      <span className="text-sm font-medium">L {totalExamenes.toFixed(2)}</span>
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {selectedExamenes.map((e) => (
                        <div
                          key={e.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2 p-2 border rounded-md text-sm"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <TestTubes className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="font-mono text-xs text-muted-foreground">
                              {e.codigo}
                            </span>
                            <span className="truncate">{e.nombre}</span>
                            <Badge variant="outline" className="text-xs shrink-0">
                              {e.area_display}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 pl-6 sm:pl-0 shrink-0">
                            {porcentajeAjuste !== 0 ? (
                              <span className="flex items-center gap-1">
                                <span className="text-muted-foreground line-through text-xs">
                                  L {Number(e.precio).toFixed(2)}
                                </span>
                                <span className="font-medium">
                                  L{" "}
                                  {calcularPrecioAjustado(
                                    Number(e.precio),
                                    porcentajeAjuste
                                  ).toFixed(2)}
                                </span>
                              </span>
                            ) : (
                              <span className="text-muted-foreground">
                                L {Number(e.precio).toFixed(2)}
                              </span>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleRemoveExamen(e.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t("ordenes.noExamsSelected")}
                  </p>
                )}
              </TabsContent>

              {/* Tab de Perfiles */}
              <TabsContent value="perfiles" className="space-y-3">
                <div className="space-y-2">
                  <Label>{t("ordenes.fields.profiles")}</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t("ordenes.searchProfiles")}
                      value={perfilSearch}
                      onChange={(e) => setPerfilSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {filteredPerfilResults && filteredPerfilResults.length > 0 && (
                    <div className="border rounded-md max-h-40 overflow-y-auto">
                      {filteredPerfilResults.map((p) => (
                        <button
                          key={p.id}
                          className="w-full text-left px-3 py-2 hover:bg-muted text-sm space-y-1"
                          onClick={() => handleAddPerfil(p)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-mono text-xs text-muted-foreground mr-2">
                                {p.codigo}
                              </span>
                              <span className="font-medium">{p.nombre}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-muted-foreground">
                                L {Number(p.precio).toFixed(2)}
                              </div>
                              {p.ahorro > 0 && (
                                <div className="text-xs text-green-600">
                                  {t("ordenes.savings")}: L {Number(p.ahorro).toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t("ordenes.includesExams", { count: p.num_examenes })}:{" "}
                            {p.examenes_nombres.join(", ")}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected profiles */}
                {selectedPerfiles.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>
                        {t("ordenes.selectedProfiles")} ({selectedPerfiles.length})
                      </Label>
                      <span className="text-sm font-medium">L {totalPerfiles.toFixed(2)}</span>
                    </div>
                    <div className="max-h-56 overflow-y-auto space-y-1">
                      {selectedPerfiles.map((p) => (
                        <div key={p.id} className="p-3 border rounded-md text-sm space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Package className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <span className="font-mono text-xs text-muted-foreground">
                                {p.codigo}
                              </span>
                              <span className="font-medium">{p.nombre}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {porcentajeAjuste !== 0 ? (
                                <span className="flex items-center gap-1">
                                  <span className="text-muted-foreground line-through text-xs">
                                    L {Number(p.precio).toFixed(2)}
                                  </span>
                                  <span className="font-medium">
                                    L{" "}
                                    {calcularPrecioAjustado(
                                      Number(p.precio),
                                      porcentajeAjuste
                                    ).toFixed(2)}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-muted-foreground">
                                  L {Number(p.precio).toFixed(2)}
                                </span>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => handleRemovePerfil(p.id)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          <div className="pl-6 space-y-1">
                            <div className="text-xs text-muted-foreground">
                              {t("ordenes.includesExams", { count: p.num_examenes })}:{" "}
                              {p.examenes_nombres.join(", ")}
                            </div>
                            {p.ahorro > 0 && (
                              <div className="flex items-center gap-4 text-xs">
                                <span className="text-muted-foreground">
                                  {t("ordenes.individualPrice")}: L{" "}
                                  {Number(p.precio_individual_total).toFixed(2)}
                                </span>
                                <span className="text-green-600 font-medium">
                                  {t("ordenes.savings")}: L {Number(p.ahorro).toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t("ordenes.noProfilesSelected")}
                  </p>
                )}
              </TabsContent>
            </Tabs>

            {/* Descuentos */}
            {(selectedExamenes.length > 0 || selectedPerfiles.length > 0) && (
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Percent className="h-4 w-4" />
                  {t("ordenes.discount.title")}
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {t("ordenes.discount.percentage")}
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="0"
                        value={descuentoPorcentaje}
                        onChange={(e) => setDescuentoPorcentaje(e.target.value)}
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                        %
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {t("ordenes.discount.lempiras")}
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                        L.
                      </span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={descuentoLempiras}
                        onChange={(e) => setDescuentoLempiras(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Total general */}
            {(selectedExamenes.length > 0 || selectedPerfiles.length > 0) && (
              <div className="p-3 bg-muted/50 rounded-md space-y-2">
                {listaPrecioActiva && (
                  <div className="flex items-center gap-2 pb-1">
                    <Badge variant="secondary" className="text-xs">
                      {formatListaPrecioLabel(
                        listaPrecioActiva.nombre,
                        listaPrecioActiva.porcentaje_ajuste
                      )}
                    </Badge>
                  </div>
                )}
                {/* Precio base */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal base</span>
                  <span>L {subtotalBase.toFixed(2)}</span>
                </div>
                {/* Ajuste por lista de precios */}
                {listaPrecioActiva && porcentajeAjuste !== 0 && (
                  <div
                    className={`flex items-center justify-between text-sm ${ajusteListaPrecio >= 0 ? "text-orange-600" : "text-green-600"}`}
                  >
                    <span>
                      {ajusteListaPrecio >= 0 ? "↑" : "↓"} {listaPrecioActiva.nombre} (
                      {porcentajeAjuste >= 0 ? "+" : ""}
                      {porcentajeAjuste}%)
                    </span>
                    <span>
                      {ajusteListaPrecio >= 0 ? "+ " : "- "}L{" "}
                      {Math.abs(ajusteListaPrecio).toFixed(2)}
                    </span>
                  </div>
                )}
                {/* Subtotal con lista aplicada */}
                {listaPrecioActiva && porcentajeAjuste !== 0 && (
                  <div className="flex items-center justify-between text-sm font-medium border-t pt-1">
                    <span className="text-muted-foreground">Subtotal ajustado</span>
                    <span>L {subtotal.toFixed(2)}</span>
                  </div>
                )}
                {/* Descuentos manuales */}
                {(pctVal > 0 || lpsVal > 0) && (
                  <>
                    {pctVal > 0 && (
                      <div className="flex items-center justify-between text-sm text-green-600">
                        <span>
                          {t("ordenes.discount.percentage")} ({pctVal}%)
                        </span>
                        <span>- L {((subtotal * pctVal) / 100).toFixed(2)}</span>
                      </div>
                    )}
                    {lpsVal > 0 && (
                      <div className="flex items-center justify-between text-sm text-green-600">
                        <span>{t("ordenes.discount.lempiras")}</span>
                        <span>- L {Math.min(lpsVal, afterPct).toFixed(2)}</span>
                      </div>
                    )}
                  </>
                )}
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calculator className="h-4 w-4" />
                    <span className="font-medium">Total</span>
                  </div>
                  <span className="text-lg font-bold">L {totalGeneral.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Campo de cortesía — aparece automáticamente cuando total = 0 */}
            {totalGeneral === 0 && (selectedExamenes.length > 0 || selectedPerfiles.length > 0) && (
              <div className="rounded-lg border border-teal-200 bg-teal-50 dark:border-teal-800 dark:bg-teal-950/30 p-3 space-y-2">
                <div className="flex items-center gap-2 text-teal-700 dark:text-teal-300 font-medium text-sm">
                  <Gift className="h-4 w-4" />
                  {t("ordenes.cortesia.badge")}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">
                    {t("ordenes.cortesia.inputLabel")} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    placeholder={t("ordenes.cortesia.inputPlaceholder")}
                    value={autorizadoPor}
                    onChange={(e) => setAutorizadoPor(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Observaciones */}
            <div className="space-y-2">
              <Label>{t("ordenes.fields.observations")}</Label>
              <Textarea
                placeholder={t("ordenes.fields.observationsPlaceholder")}
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !pacienteId ||
                (selectedExamenes.length === 0 && selectedPerfiles.length === 0) ||
                (totalGeneral === 0 && !autorizadoPor.trim()) ||
                createMutation.isPending ||
                marcarCortesiaMutation.isPending
              }
            >
              {createMutation.isPending || marcarCortesiaMutation.isPending
                ? t("common.saving")
                : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={showExitConfirm}
        onOpenChange={setShowExitConfirm}
        title={t("ordenes.unsavedChanges.title")}
        description={t("ordenes.unsavedChanges.description")}
        onConfirm={handleConfirmExit}
        variant="destructive"
        confirmText={t("ordenes.unsavedChanges.confirm")}
        cancelText={t("ordenes.unsavedChanges.cancel")}
      />

      {/* Paso 1: ¿Desea pagar ahora? */}
      <ConfirmDialog
        open={postCreacion.step === "confirm-pago"}
        onOpenChange={(v) => {
          if (!v && !confirmPagoConfirmedRef.current) {
            setPostCreacion((s) => ({ ...s, step: "recibo" }));
          }
          confirmPagoConfirmedRef.current = false;
        }}
        title={t("ordenes.postCreacion.pagarTitle")}
        description={t("ordenes.postCreacion.pagarDescription", {
          numero: postCreacion.orden?.numero ?? "",
        })}
        onConfirm={() => {
          confirmPagoConfirmedRef.current = true;
          setPostCreacion((s) => ({ ...s, step: "pago" }));
        }}
        confirmText={t("ordenes.postCreacion.pagarConfirm")}
        cancelText={t("ordenes.postCreacion.pagarSkip")}
      />

      {/* Paso 2: Formulario de pago */}
      {postCreacion.orden && (
        <PagoOrdenFormDialog
          open={postCreacion.step === "pago"}
          onOpenChange={(v) => {
            if (!v) setPostCreacion((s) => ({ ...s, step: "recibo" }));
          }}
          orden={(() => {
            const o = postCreacion.orden!;
            return {
              id: o.id,
              numero: o.numero,
              paciente_nombre: o.paciente_nombre,
              total: o.saldo_pendiente,
              saldo_pendiente: o.saldo_pendiente,
              total_pagado: o.total_pagado,
            } as OrdenListItem;
          })()}
          onPagoSuccess={() => setPostCreacion((s) => ({ ...s, step: "recibo" }))}
        />
      )}

      {/* Paso 3: Recibo */}
      {postCreacion.orden && (
        <ReciboPostCreacionDialog
          open={postCreacion.step === "recibo"}
          onClose={() => {
            setPostCreacion({ step: "idle", orden: null });
            onSuccess();
          }}
          ordenId={postCreacion.orden.id}
          ordenNumero={postCreacion.orden.numero}
          pacienteNombre={postCreacion.orden.paciente_nombre}
          totalExamenes={postCreacion.orden.total_examenes}
        />
      )}
    </>
  );
}
